/**
 * 项目孵化器领域逻辑（扩展⑦）：方向发现 + 沙盘预演。
 *
 * 设计红线（与匹配引擎同一套诚实哲学，答辩可直接讲）：
 * - 聚类是**确定性算法**（并查集：共享技能/拉丁词/非通用中文 bigram 即同簇），不是"AI 觉得它们像"；
 * - 沙盘 simulateOutcome 的公式常数全部写死在本文件，输出标注「模拟分，不是概率预测」；
 * - LLM 在本模块只负责"把一句原话归一成标签与关键词"（且有规则兜底 + via 标注），
 *   不参与聚类决策、不生成任何数字。
 */

import { SKILLS } from './skills.js';

// ---------------------------------------------------------------------------
// 方向原话归一（规则路径，LLM 不可用时的兜底）
// ---------------------------------------------------------------------------

export interface IdeaNormalized {
  /** 方向短标签（≤20 字），供聚类命名与卡片展示 */
  label: string;
  /** 聚类关键词（技能 id、拉丁词、非通用中文 bigram） */
  keywords: string[];
  /** 命中技能词表的技能 id（与匹配引擎同一词表，绝不自造） */
  skills: string[];
}

/** 归一化时剔除的功能词（ particle / 常见动词 / 指代词）：命中这些位置才切分中文片段。 */
const SPLIT_CHARS = '我想你做他她它这那有的了和与在把被把用通过基于关于对于为了就是都很也还又之其为于以而且吗呢吧啊哦嘛呀嘛呗咯嘛哩呐嚒';
const HAN = /\p{Script=Han}/u;

/** 聚类时视为"太通用、不足以判同簇"的 bigram（出现率高的名词骨架）。 */
const COMMON_BIGRAMS = new Set([
  '我们', '一个', '项目', '平台', '系统', '工具', '小程序', '学生', '校园', '大学',
  '服务', '管理', '信息', '数据', '智能', '在线', '学习', '生活', '提供', '解决',
  '问题', '需求', '功能', '用户', '可以', '进行', '相关', '实现', '开发', '设计',
  '比赛', '作品', '方向', '方案', '高校', '同学', '老师',
]);

/** 技能词表索引：id / 中文名 / 别名 → id（与 extract.service 的 resolveSkillWord 同一口径）。 */
function resolveSkillWord(word: string): string | null {
  const text = word.trim().toLowerCase();
  if (text.length < 2) return null;
  for (const skill of SKILLS) {
    if (skill.id.toLowerCase() === text || skill.name.toLowerCase() === text) return skill.id;
    for (const alias of skill.aliases ?? []) {
      const aliasLower = alias.toLowerCase();
      if (aliasLower === text) return skill.id;
      if (/^[a-z0-9]/.test(aliasLower)) {
        const escaped = aliasLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        if (new RegExp(`(?<![a-z0-9])${escaped}(?![a-z0-9])`).test(text)) return skill.id;
      }
    }
  }
  return null;
}

/** 中文片段 → bigram 序列（"校园二手书" → ["校园","园二","二手","手书"]）。 */
function bigramsOf(segment: string): string[] {
  const out: string[] = [];
  for (let i = 0; i + 1 < segment.length; i += 1) {
    const gram = segment.slice(i, i + 2);
    if (!COMMON_BIGRAMS.has(gram)) out.push(gram);
  }
  return out;
}

/**
 * 规则归一：从一句自由文本提取 标签 / 关键词 / 技能。
 * 纯函数、零依赖、可测试；LLM 路径失败时由它兜底（via='rule'）。
 */
export function normalizeIdeaText(text: string): IdeaNormalized {
  const skills: string[] = [];
  const keywords: string[] = [];
  const push = (word: string): void => {
    if (word.length >= 2 && !keywords.includes(word)) keywords.push(word);
  };

  // 技能：整句做词边界扫描（拉丁别名）+ 全等匹配（中文名）
  for (const match of text.matchAll(/[A-Za-z][A-Za-z0-9.+-]{1,19}/g)) {
    const resolved = resolveSkillWord(match[0]);
    if (resolved !== null && !skills.includes(resolved)) skills.push(resolved);
  }
  for (const skill of SKILLS) {
    if (skills.includes(skill.id)) continue;
    if (skill.name.length >= 2 && text.includes(skill.name)) skills.push(skill.id);
    for (const alias of skill.aliases ?? []) {
      if (skills.includes(skill.id)) break;
      if (alias.length >= 2 && HAN.test(alias) && text.includes(alias)) {
        skills.push(skill.id);
        break;
      }
    }
  }

  // 拉丁词（非技能）作为关键词
  for (const match of text.matchAll(/[A-Za-z][A-Za-z0-9]{1,19}/g)) {
    push(match[0].toLowerCase());
  }

  // 中文片段：按功能词切分后取 bigram
  const hanRuns = text.match(/[\p{Script=Han}]{2,}/gu) ?? [];
  for (const run of hanRuns) {
    const fragments = run.split(new RegExp(`[${SPLIT_CHARS}]`)).filter((piece) => piece.length >= 2);
    for (const fragment of fragments) {
      // 短片段整体保留（"二手书置换"），长片段只留 bigram 防整句误判同簇
      if (fragment.length <= 6) push(fragment);
      for (const gram of bigramsOf(fragment)) push(gram);
    }
    if (run.length <= 6) push(run);
  }

  // 标签：第一个 ≥4 字、含中文的功能词切分片段；退而求其次取原文前 12 字
  let label = '';
  for (const run of hanRuns) {
    const candidate = run.split(new RegExp(`[${SPLIT_CHARS}]`)).find((piece) => piece.length >= 4 && piece.length <= 20);
    if (candidate !== undefined) {
      label = candidate;
      break;
    }
  }
  if (label === '') label = text.replace(/\s+/g, ' ').trim().slice(0, 12);
  return { label: label.slice(0, 20), keywords, skills };
}

// ---------------------------------------------------------------------------
// 确定性聚类（并查集）
// ---------------------------------------------------------------------------

export interface IdeaClusterInput {
  id: string;
  label: string;
  keywords: string[];
  skills: string[];
}

export interface IdeaCluster {
  /** 稳定簇键：成员 idea id 字典序拼接（新增"桥接"想法可能并簇，键随之变化，投票以当时键为准） */
  key: string;
  /** 簇标签：取簇内最早（created 升序第一个）想法的归一标签 */
  label: string;
  ideaIds: string[];
  skills: string[];
}

/** 两条想法是否同簇：共享技能 id / 拉丁关键词 / 非通用 bigram，任一命中即同簇。 */
function sameCluster(a: IdeaClusterInput, b: IdeaClusterInput): boolean {
  for (const skill of a.skills) if (b.skills.includes(skill)) return true;
  const aLatin = new Set(a.keywords.filter((word) => /^[a-z0-9]/i.test(word)));
  for (const word of b.keywords) {
    if (aLatin.has(word.toLowerCase())) return true;
  }
  const aHan = new Set(a.keywords.filter((word) => HAN.test(word)));
  for (const word of b.keywords) {
    if (HAN.test(word) && aHan.has(word)) return true;
  }
  return false;
}

/**
 * 并查集聚类：O(n²) 成对判定，n 为单队想法数（演示规模 <50，毫无压力）。
 * 完全确定性：输入顺序无关（内部按 id 字典序归一后合并）。
 */
export function clusterIdeas(ideas: IdeaClusterInput[]): IdeaCluster[] {
  const sorted = [...ideas].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const parent = new Map<string, string>();
  const find = (x: string): string => {
    let root = x;
    while (parent.get(root) !== root) root = parent.get(root) ?? root;
    parent.set(x, root);
    return root;
  };
  const union = (a: string, b: string): void => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };
  for (const idea of sorted) parent.set(idea.id, idea.id);
  for (const [index, a] of sorted.entries()) {
    for (const b of sorted.slice(index + 1)) {
      if (sameCluster(a, b)) union(a.id, b.id);
    }
  }

  const groups = new Map<string, IdeaClusterInput[]>();
  for (const idea of sorted) {
    const root = find(idea.id);
    const list = groups.get(root) ?? [];
    list.push(idea);
    groups.set(root, list);
  }

  const clusters: IdeaCluster[] = [];
  for (const members of groups.values()) {
    const first = members[0];
    if (first === undefined) continue; // 空簇不可能出现，防御性跳过
    // members 已按 id 字典序（输入 sorted）：展示顺序由服务层按 created 重排，这里只管成簇
    const skills: string[] = [];
    for (const member of members) {
      for (const skill of member.skills) if (!skills.includes(skill)) skills.push(skill);
    }
    clusters.push({
      key: members.map((member) => member.id).sort().join('+'),
      label: first.label,
      ideaIds: members.map((member) => member.id),
      skills,
    });
  }
  // 输出顺序也确定：按簇内最小 idea id 字典序
  clusters.sort((a, b) => (a.key < b.key ? -1 : 1));
  return clusters;
}

// ---------------------------------------------------------------------------
// 项目沙盘（what-if 模拟器）
// ---------------------------------------------------------------------------

export interface SimulatorInput {
  /** 每人每周可投入小时（0–60） */
  weeklyHoursPerMember: number;
  /** 队伍人数（1–10） */
  teamSize: number;
  /** 方向与现有技能契合度 0–100（可来自缺口雷达：已覆盖/需求） */
  skillMatchPct: number;
  /** 距下一个赛事节点天数（0–120） */
  daysToDeadline: number;
  /** 风险事件：考试周（产能 ×0.85） */
  riskExamWeek: boolean;
  /** 风险事件：有成员可能退出（产能 ×0.80） */
  riskMemberLeave: boolean;
  /** 风险事件：中途换方向（产能 ×0.75） */
  riskPivot: boolean;
}

export interface SimulatorResult {
  /** 完成指数 0–100（模拟分，不是概率预测） */
  readinessPct: number;
  /** 风险档位 */
  riskLevel: 'low' | 'mid' | 'high';
  /** 预计可用产能（人时） */
  capacityUnits: number;
  /** 按技能契合度折算的所需产能（人时） */
  neededUnits: number;
  /** 确定性建议（按当前最紧约束逐条给出） */
  advice: string[];
}

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

/** 需求侧基准：技能 100% 契合需 60 单位，0% 契合需 180 单位（线性）。 */
const NEEDED_BASE = 180;
const NEEDED_FIT_SLOPE = 1.2; // NEEDED_BASE - skillMatch% * NEEDED_FIT_SLOPE
const CAPACITY_EXAM_FACTOR = 0.85;
const CAPACITY_LEAVE_FACTOR = 0.8;
const CAPACITY_PIVOT_FACTOR = 0.75;

/**
 * 沙盘模拟：capacity = 每周小时 × 人数 × 剩余周数 × 风险折扣；
 *           needed = 180 − 契合度 × 1.2；readiness = clamp01(capacity / needed)。
 * 所有常数写死在本文件，前端只画图不算分。
 */
export function simulateOutcome(input: SimulatorInput): SimulatorResult {
  const weeklyHours = clamp(Math.round(input.weeklyHoursPerMember), 0, 60);
  const teamSize = clamp(Math.round(input.teamSize), 1, 10);
  const skillMatch = clamp(Math.round(input.skillMatchPct), 0, 100);
  const days = clamp(Math.round(input.daysToDeadline), 0, 120);

  const weeksLeft = days / 7;
  let capacity = weeklyHours * teamSize * weeksLeft;
  if (input.riskExamWeek) capacity *= CAPACITY_EXAM_FACTOR;
  if (input.riskMemberLeave) capacity *= CAPACITY_LEAVE_FACTOR;
  if (input.riskPivot) capacity *= CAPACITY_PIVOT_FACTOR;

  const needed = NEEDED_BASE - skillMatch * NEEDED_FIT_SLOPE;
  const raw = needed <= 0 ? 1 : capacity / needed;
  const readinessPct = Math.round(clamp(raw, 0, 1) * 100);
  const riskLevel: SimulatorResult['riskLevel'] = readinessPct >= 75 ? 'low' : readinessPct >= 45 ? 'mid' : 'high';

  const advice: string[] = [];
  if (skillMatch < 50) {
    advice.push('方向与现有技能契合度偏低：先在「缺口雷达」确认要补谁，或考虑调整方向');
  }
  if (weeklyHours < 6) {
    advice.push('每周投入偏低：在当前时间窗内，时间是最紧的约束');
  }
  if (days <= 21) {
    advice.push('距节点不足三周：建议砍范围保交付，把 demo 做扎实');
  }
  if (input.riskExamWeek) advice.push('考试周折扣已计入：提前把重活排在考试周之前');
  if (input.riskMemberLeave) advice.push('成员退出风险已计入：关键模块至少两人能接手');
  if (input.riskPivot) advice.push('换方向代价很高（产能 ×0.75）：现在定的方向尽量别再换');
  if (advice.length === 0) {
    advice.push(readinessPct >= 75 ? '按当前节奏推进即可，保持决策日志的更新频率' : '整体可控，优先补契合度最低的短板技能');
  }

  return {
    readinessPct,
    riskLevel,
    capacityUnits: Math.round(capacity),
    neededUnits: Math.round(needed),
    advice,
  };
}

/** 沙盘公式的人话版（前端口径声明与答辩引用同一来源）。 */
export function describeSimulatorFormula(): string {
  return [
    '完成指数 = clamp01(产能 / 需求) × 100；',
    `产能 = 每人每周小时 × 人数 × 剩余周数 × 风险折扣（考试周 ×${CAPACITY_EXAM_FACTOR} / 成员退出 ×${CAPACITY_LEAVE_FACTOR} / 换方向 ×${CAPACITY_PIVOT_FACTOR}）；`,
    `需求 = ${NEEDED_BASE} − 技能契合度 × ${NEEDED_FIT_SLOPE}（契合度越高，干同样的活得花的时间越少）。`,
  ].join('');
}
